import {
    Input,
    Directive,
    inject,
    ElementRef,
    Renderer2,
    OnInit,
    OnDestroy,
    OnChanges,
    SimpleChanges,
    ChangeDetectorRef,
    Output,
    EventEmitter
} from '@angular/core';
import { Product } from '@df/catalog/product/product';
import { Subscription } from 'rxjs';
import { TradePricingService } from './trade-pricing.service';
import { CdService } from '@df/ng/cd.service';
import { TimeoutService } from '@df/ng/timeout.service';
import { PriceService } from '@df/ui/price/price.service';

/**
 * Mask for product price
 */
@Directive({
    selector: '[tradePricingMask]',
    standalone: true
})
export class TradePricingMaskDirective implements OnChanges, OnInit, OnDestroy {
    @Input({
        required: true
    })
    tradePricingMask!: Product[];

    @Output()
    tradePricingMaskChange = new EventEmitter<void>();

    /**
     * Element reference
     */
    private elementRef = inject(ElementRef);

    /**
     * Renderer
     */
    private renderer = inject(Renderer2);

    /**
     * Trade price service
     */
    private tradePricingService = inject(TradePricingService);

    /**
     * Change detector
     */
    private changeDetectorRef = inject(ChangeDetectorRef);

    /**
     * Services
     */
    private timeoutService = inject(TimeoutService);
    private cdService = inject(CdService);
    private priceService = inject(PriceService);

    /**
     * Change subscriptions
     */
    private productSubscriptions?: Subscription;
    private subscriptions = new Subscription();

    /**
     * Can display state applied
     */
    private canDisplay?: boolean;

    /**
     * Initiated flag
     */
    private initiated = false;

    ngOnInit(): void {
        this.initiated = true;
        this.subscriptions.add(
            this.priceService.catalogPricesInclTax$.subscribe(value => {
                if (this.initiated) {
                    this.emitChange();
                }
            })
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tradePricingMask']) {
            this.productSubscriptions?.unsubscribe();
            this.productSubscriptions = new Subscription();

            this.tradePricingMask.forEach(product => {
                const price$ = this.tradePricingService.getPrice$(product);
                if (price$) {
                    this.productSubscriptions!.add(price$.subscribe(() => this.emitChange()));
                }
            });

            this.productSubscriptions.add(
                this.tradePricingService.getCanDisplay$(this.tradePricingMask).subscribe(canDisplay => {
                    if (this.canDisplay !== canDisplay) {
                        this.canDisplay = canDisplay;
                        if (canDisplay) {
                            this.renderer.removeClass(this.elementRef.nativeElement, 'price-mask');
                        } else {
                            this.renderer.addClass(this.elementRef.nativeElement, 'price-mask');
                        }

                        if (this.initiated) {
                            this.emitChange();
                        }
                    }
                })
            );
        }
    }

    private emitChange = this.timeoutService.debounce(() => {
        this.cdService.push(this.changeDetectorRef);
        this.tradePricingMaskChange.emit();
    });

    ngOnDestroy(): void {
        this.productSubscriptions?.unsubscribe();
        this.subscriptions?.unsubscribe();
    }
}
